import React from 'react';
import {
  Badge,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import SettingsRoundedIcon from '@mui/icons-material/SettingsRounded';

import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import WarningRoundedIcon from '@mui/icons-material/WarningRounded';
import ErrorRoundedIcon from '@mui/icons-material/ErrorRounded';
import PriorityHighRoundedIcon from '@mui/icons-material/PriorityHighRounded';
import HelpOutlineRoundedIcon from '@mui/icons-material/HelpOutlineRounded';

function getStatusIcon(status) {
  switch (status) {
    case 'Success':
      return { Color: 'success', StatusIcon: CheckCircleRoundedIcon };
    case 'Warning':
      return { Color: 'warning', StatusIcon: WarningRoundedIcon };
    case 'Error':
      return { Color: 'error', StatusIcon: ErrorRoundedIcon };
    case 'Critical':
      return { Color: 'error', StatusIcon: PriorityHighRoundedIcon };
    default:
      return { Color: 'default', StatusIcon: HelpOutlineRoundedIcon };
  }
}

export const ConfigurationHealthCheckDialog = ({
  healthCheck,
  size = 'small',
}) => {
  const [open, setOpen] = React.useState(false);

  // Only handle ConfigurationHealthCheck
  if (!healthCheck || healthCheck.Name !== 'ConfigurationHealthCheck')
    return null;

  // Hide completely when Status is Success
  if (healthCheck.Status === 'Success') return null;

  const key = 'ConfigurationHealthCheck';
  const label = 'Configuration health check';
  const Icon = SettingsRoundedIcon;

  const { StatusIcon: TopStatusIcon, Color: TopColor } = getStatusIcon(
    healthCheck.Status,
  );

  // Flatten Errors + Warnings
  const messages = [
    ...(healthCheck.Errors || []).map((e) => ({
      severity: 'error',
      type: e.error_type,
      typeLabel: 'Error',
      text: e.message,
    })),
    ...(healthCheck.Warnings || []).map((w) => ({
      severity: 'warning',
      type: w.warning_type,
      typeLabel: 'Warning',
      text: w.message,
    })),
  ];

  return (
    <>
      {/* ICON BUTTON (shown when Status !== Success) */}
      <Tooltip title={label} arrow placement="top">
        <Badge
          overlap="circular"
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          badgeContent={
            <TopStatusIcon
              sx={{
                fontSize: 14,
                color: (theme) =>
                  TopColor === 'default'
                    ? theme.palette.text.disabled
                    : theme.palette[TopColor].main,
              }}
            />
          }
        >
          <IconButton
            size={size}
            onClick={(e) => {
              e.stopPropagation();
              setOpen(true);
            }}
            sx={{ border: 1, borderColor: 'divider' }}
            aria-label={key}
          >
            <Icon />
          </IconButton>
        </Badge>
      </Tooltip>

      {/* DIALOG */}
      <Dialog
        key={key}
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <ListItem disableGutters disablePadding>
            <ListItemIcon>
              <Badge
                overlap="circular"
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                badgeContent={
                  <TopStatusIcon
                    sx={{
                      fontSize: 14,
                      color: (theme) =>
                        TopColor === 'default'
                          ? theme.palette.text.disabled
                          : theme.palette[TopColor].main,
                    }}
                  />
                }
              >
                <IconButton
                  size={size}
                  disabled
                  sx={{
                    border: 1,
                    borderColor: 'divider',
                    ':disabled': { color: 'text.secondary' },
                  }}
                  aria-label={key}
                >
                  <Icon />
                </IconButton>
              </Badge>
            </ListItemIcon>
            <ListItemText primary={label} />
          </ListItem>
        </DialogTitle>

        <DialogContent dividers>
          {messages.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No details available.
            </Typography>
          ) : (
            <Box sx={{ display: 'grid', gap: 1 }}>
              {messages.map((m, i) => {
                const { StatusIcon, Color } = getStatusIcon(
                  m.severity === 'error' ? 'Error' : 'Warning',
                );

                return (
                  <Stack
                    key={i}
                    direction="row"
                    spacing={1.25}
                    alignItems="flex-start"
                    borderBottom={
                      i < messages.length - 1 ? '1px solid' : 'none'
                    }
                    borderColor="divider"
                    pb={1}
                  >
                    <ListItem disableGutters>
                      <ListItemIcon>
                        <StatusIcon
                          sx={{
                            color: (theme) =>
                              Color === 'default'
                                ? theme.palette.text.disabled
                                : theme.palette[Color].main,
                          }}
                        />
                      </ListItemIcon>
                      <ListItemText
                        primary={m.typeLabel || m.type || 'Message'}
                        secondary={m.text}
                      />
                    </ListItem>
                  </Stack>
                );
              })}
            </Box>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
export default ConfigurationHealthCheckDialog;
